// src/components/chatPolicy/UserTypeahead.jsx
// Multi-select de usuarios con búsqueda backend (typeahead) + filtros opcionales
// por unidad, sucursal, país y rol. Si pasas `catalog`, se renderizan dropdowns
// arriba del input. Si no, solo input libre.
import React, { useEffect, useRef, useState } from 'react';
import { Form, Spinner, Badge } from 'react-bootstrap';
import { searchUsers } from '../../services/chatPolicyApi';

const ROLES = [
    { v: '',         label: 'Todos los roles' },
    { v: 'admin',    label: 'Admin' },
    { v: 'leader',   label: 'Líder' },
    { v: 'agent',    label: 'Agente' },
    { v: 'authorizer', label: 'Autorizador' },
    { v: 'user',     label: 'Usuario final' },
];

export default function UserTypeahead({ value = [], onChange, placeholder = 'Buscar usuarios…', catalog = null }) {
    const [q, setQ] = useState('');
    const [filters, setFilters] = useState({ unidad: '', sucursal: '', pais: '', role: '' });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        const hasFilter = filters.unidad || filters.sucursal || filters.pais || filters.role;
        if ((!q || q.length < 2) && !hasFilter) { setResults([]); return; }
        let alive = true;
        setLoading(true);
        const t = setTimeout(() => {
            searchUsers(q, filters)
                .then(rows => { if (alive) setResults(rows || []); })
                .catch(() => { if (alive) setResults([]); })
                .finally(() => { if (alive) setLoading(false); });
        }, 200);
        return () => { alive = false; clearTimeout(t); };
    }, [q, filters]);

    useEffect(() => {
        const onClickOut = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClickOut);
        return () => document.removeEventListener('mousedown', onClickOut);
    }, []);

    const pick = (u) => {
        if (value.some(v => String(v._id) === String(u._id))) return;
        onChange?.([...value, u]);
        setQ('');
    };
    const remove = (id) => onChange?.(value.filter(v => String(v._id) !== String(id)));

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            {value.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    {value.map(u => (
                        <Badge key={u._id} bg="primary" style={{ padding: '6px 8px', fontWeight: 500 }}>
                            {u.name}
                            {u.puesto && <span style={{ opacity: 0.8, marginLeft: 4 }}>· {u.puesto}</span>}
                            <span role="button" onClick={() => remove(u._id)} style={{ cursor: 'pointer', marginLeft: 6 }}>✕</span>
                        </Badge>
                    ))}
                </div>
            )}

            {catalog && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 4 }}>
                    <Form.Select size="sm" value={filters.unidad}
                        onChange={e => setFilters(f => ({ ...f, unidad: e.target.value }))}
                        style={{ borderRadius: 6, fontSize: 11 }}>
                        <option value="">Todas las unidades</option>
                        {(catalog.unidades || []).map(u => <option key={u} value={u}>{u}</option>)}
                    </Form.Select>
                    <Form.Select size="sm" value={filters.sucursal}
                        onChange={e => setFilters(f => ({ ...f, sucursal: e.target.value }))}
                        style={{ borderRadius: 6, fontSize: 11 }}>
                        <option value="">Todas las sucursales</option>
                        {(catalog.sucursales || []).map(s => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                    <Form.Select size="sm" value={filters.pais}
                        onChange={e => setFilters(f => ({ ...f, pais: e.target.value }))}
                        style={{ borderRadius: 6, fontSize: 11 }}>
                        <option value="">Todos los países</option>
                        {(catalog.paises || []).map(p => <option key={p} value={p}>{p}</option>)}
                    </Form.Select>
                    <Form.Select size="sm" value={filters.role}
                        onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
                        style={{ borderRadius: 6, fontSize: 11 }}>
                        {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                    </Form.Select>
                </div>
            )}

            <Form.Control
                size="sm"
                placeholder={placeholder}
                value={q}
                onChange={e => { setQ(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                style={{ borderRadius: 8 }}
            />
            {open && (q.length >= 2 || filters.unidad || filters.sucursal || filters.pais || filters.role) && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'var(--c-card, #fff)', border: '1px solid var(--c-border, #e5e7eb)',
                    borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,.1)',
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 12 }}><Spinner size="sm" animation="border" /></div>
                    ) : results.length === 0 ? (
                        <div style={{ padding: 12, fontSize: 12, color: 'var(--c-text-4)' }}>Sin resultados.</div>
                    ) : results.map(u => (
                        <div key={u._id} onClick={() => pick(u)} style={{
                            padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--c-border, #f1f5f9)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>
                                {u.puesto || u.role}
                                {u.unidad ? ` · ${u.unidad}` : ''}
                                {u.sucursal ? ` · ${u.sucursal}` : ''}
                                {u.pais ? ` · ${u.pais}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
